import { popularList } from "@/constants/apiConstantData";
import { useProfile } from "@/context/useProfileProps";
import { extractValues, handleFocusOnField } from "@/helpers/helper";
import { getSiteProps } from "@/utils";
import { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";

import { MultiDropDownController } from "@/components/common/dropdownController";
import ProfileModal from "@/components/common/profileModal";

import { useUpdatePreference } from "./query";
import { DropdownOptionType, EditProps } from "./types";

const EditCard = ({
  isEdit,
  closeEdit,
  prefilledData,
  isCalledFromOutside = false, // set true if calling from outside index.tsx
  formRef,
  handleFooterBtnLoading, // pass only if calling from outside index.tsx
  focusOnField = "",
}: EditProps) => {
  const { locations, roles } =
    prefilledData?.jobPreferenceSection?.jobPreference ?? {};
  const defaultValues = { locations: locations || [], roles: roles || [] };

  const { mutate } = useUpdatePreference();

  const {
    isMobile,
    showToast,
    handleEventTracking,
    translation: { preference, errorMessages },
  } = useProfile();
  const [isSaveLoading, setIsSaveLoading] = useState(false);

  const siteProps = getSiteProps(window.location.hostname);
  const channel = siteProps?.site_map || "rexmonster";

  const suggestedOptions = {
    list: popularList(channel).location,
    type: "Popular Locations",
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    clearErrors,
    getValues,
  } = useForm<FieldValues>({ defaultValues });

  useEffect(() => {
    handleFocusOnField(focusOnField);
  }, [focusOnField]);

  const handleSaveBtnLoading = (val: boolean) => {
    setIsSaveLoading(val);
    handleFooterBtnLoading && handleFooterBtnLoading({ isSaveLoading: val });
  };

  const onSubmit = (data: any) => {
    handleSaveBtnLoading(true);
    const payload = {
      jobPreference: {
        locations: extractValues(data.locations, ["id"]),
        roles: extractValues(data.roles, ["id"]),
      },
    };
    mutate(
      { data: payload, action: "edit" },
      {
        onSuccess: () => {
          handleSaveBtnLoading(false);
          showToast({
            show: true,
            message: "Job preferences updated successfully",
            type: "success",
          });
          let urlParams = new URLSearchParams(window.location.search);
          const isApp = urlParams.get("app") === "true";
          handleEventTracking({
            eventType: "eventPush",
            eventName: "ProfilePage_Section_Saved",
            eventProps: {
              Job_Preferences_Saved: JSON.stringify(payload),
              Source: isApp ? "app" : "web",
            },
          });
          closeEdit("save");
        },
        onError: (err) => {
          handleSaveBtnLoading(false);
          showToast({
            show: true,
            message:
              err instanceof Error
                ? err.message
                : errorMessages.somethingWentWrong,
            type: "error",
          });
        },
      },
    );
  };

  const handleSelect = (name: string, value: DropdownOptionType) => {
    clearErrors(name);
    setValue(name, value);
  };

  const renderForm = (
    <form
      className="flex h-full flex-col gap-6"
      onSubmit={handleSubmit(onSubmit)}
      ref={formRef}
    >
      <MultiDropDownController
        isRequired={true}
        name="roles"
        maxSelect={5}
        control={control}
        isSearchable={true}
        type="DESIGNATION"
        label={preference.preferredRoles}
        onSelect={(value) => handleSelect("roles", value)}
        errorMessage={errors.roles ? errorMessages.fieldRequired : ""}
        placeholder={
          getValues("roles") && getValues("roles").length >= 5
            ? "Maximum 5 preferred job titles can be selected"
            : preference.jobTitlePlaceholder
        }
      />

      <MultiDropDownController
        isRequired={true}
        name="locations"
        maxSelect={5}
        control={control}
        isSearchable={true}
        type="LOCATION"
        label={preference.preferredLocations}
        onSelect={(value) => handleSelect("locations", value)}
        errorMessage={errors.locations ? errorMessages.fieldRequired : ""}
        suggestedOptions={suggestedOptions}
        placeholder={
          getValues("locations") && getValues("locations").length >= 5
            ? "Maximum 5 preferred locations can be selected"
            : preference.locationPlaceholder
        }
      />
    </form>
  );

  return !isCalledFromOutside ? (
    <ProfileModal
      id="preference-edit-modal"
      showModal={isEdit}
      closeModal={closeEdit}
      isMobile={isMobile}
      customClass="h-[80vh]"
      showCloseButton={true}
      screenAlignment={isMobile ? "bottom-sheet" : "center"}
      showHandleBar={isMobile}
      heading={preference.title}
      footerPrimaryBtnLabel="Save"
      footerPrimaryBtnHandler={() => formRef.current?.requestSubmit()}
      footerPrimaryBtnIsLoading={isSaveLoading}
    >
      {renderForm}
    </ProfileModal>
  ) : (
    renderForm
  );
};

export default EditCard;
